import React from "react";
import isPropsEqual from "react-fast-compare";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCellComponent, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { useNumberFormatter } from "../../hooks";

class TableCell extends React.Component<TableCellProps> {
  shouldComponentUpdate(nextProps: TableCellProps) {
    const { children: nextChildren } = nextProps; // eslint-disable-line
    const { children: thisChildren } = this.props; // eslint-disable-line

    if (typeof thisChildren === "string") return nextChildren !== thisChildren;
    if (
      typeof thisChildren === "object" &&
      (thisChildren as any).type === "span"
    ) {
      const { props: nextProps } = nextChildren as any;
      const { props: thisProps } = thisChildren as any;
      return nextProps.children !== thisProps.children; // eslint-disable-line
    }

    return true;
  }

  render() {
    return <TableCellComponent {...this.props} />;
  }
}

interface Props {
  minPremium: number;
  sumInsuredPremium: number;
  optionalExtras: number;
  subTotal: number;
  stampDuty: number;
  grandTotal: number;
}

function WorkersCompensationCalculations({
  minPremium,
  sumInsuredPremium,
  optionalExtras,
  subTotal,
  stampDuty,
  grandTotal,
}: Props) {
  const moneyFormatter = useNumberFormatter();

  const calculations = [
    {
      id: "sumInsuredPremium",
      name: "Sum Insured Premium",
      value: sumInsuredPremium,
    },
    {
      id: "optionalExtras",
      name: "Plus Optional Extras",
      value: optionalExtras,
      indent: true,
    },
    {
      id: "subTotal",
      name: `Sub Total (Minimum premium is ${moneyFormatter.format(
        minPremium
      )})`,
      value: subTotal,
      indent: true,
    },
    {
      id: "stampDuty",
      name: "Stamp Duty",
      value: stampDuty,
    },
    {
      id: 8,
      name: "Grand Total",
      value: grandTotal,
      isTotal: true,
    },
  ];

  return (
    <TableContainer>
      <Table aria-label="calculations table">
        <TableBody>
          {calculations.map(({ id, ...row }) => (
            <TableRow
              key={id}
              sx={{
                ...((row as any).isTotal && {
                  backgroundColor: "#e2e2e2",
                  borderTop: "2px solid #222",
                }),
              }}
            >
              <TableCell>
                <Box
                  component="span"
                  sx={{
                    ...((row as any).indent && {
                      display: "block",
                      pl: 2,
                    }),
                    ...((row as any).isTotal && {
                      fontWeight: "bold",
                      fontSize: "1.1em",
                    }),
                  }}
                >
                  {row.name}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={
                  row.isTotal
                    ? {
                        fontWeight: "bold",
                        fontSize: "1.1em",
                      }
                    : {
                        fontWeight: "normal",
                      }
                }
              >
                {moneyFormatter.format(row.value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default React.memo(WorkersCompensationCalculations, isPropsEqual);
